/**
 * <AuthBlockedNotice/> — friendly full-screen block screen shown when
 * the backend rejects a login/register attempt with a 403 code of
 * ``vpn_blocked`` or ``ip_blocked``. The user should NEVER see a raw
 * error toast for these — the screen explains what happened, shows
 * the exact ASN/Org (helps power users disable the right connection),
 * and offers a "Try again" button.
 *
 * Two flavours:
 *   • vpn_blocked   → "You're on a VPN" (transient — disable & retry)
 *   • ip_blocked    → "Too many failed attempts" (permanent or temporary)
 *
 * Renders inline (not a modal) so screen readers get the message and
 * mobile users don't get an overlay they can dismiss by accident.
 */
import { Button } from "@/components/ui/button";
import { ShieldAlert, ShieldOff, RefreshCw } from "lucide-react";

function formatExpires(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const now = new Date();
    const mins = Math.max(1, Math.round((d - now) / 60000));
    if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"}`;
    const hrs = Math.round(mins / 60);
    return `${hrs} hour${hrs === 1 ? "" : "s"}`;
  } catch {
    return null;
  }
}

export default function AuthBlockedNotice({ detail, onRetry }) {
  if (!detail || typeof detail !== "object") return null;
  const isVpn = detail.code === "vpn_blocked";
  const isIp  = detail.code === "ip_blocked";
  if (!isVpn && !isIp) return null;

  if (isVpn) {
    return (
      <div
        data-testid="auth-blocked-vpn"
        className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-left"
      >
        <div className="flex items-start gap-3">
          <ShieldOff className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h2 className="font-serif-display text-xl text-amber-900">
              You&apos;re on a VPN or datacenter connection
            </h2>
            <p className="mt-2 text-sm text-amber-900/90 leading-relaxed">
              For your safety and everyone else&apos;s, HustlePlaza
              doesn&apos;t allow logins from VPN or hosting IPs.
              Disable your VPN for 30&nbsp;seconds, then hit
              <em> Try again</em> — you don&apos;t need to close this tab.
            </p>
            <dl className="mt-4 text-xs bg-white/60 rounded-lg border border-amber-200 divide-y divide-amber-100">
              <div className="flex justify-between px-3 py-2">
                <dt className="text-amber-900/70">Detected IP</dt>
                <dd
                  data-testid="auth-blocked-vpn-ip"
                  className="font-mono text-amber-950"
                >
                  {detail.ip || "—"}
                </dd>
              </div>
              {detail.asn ? (
                <div className="flex justify-between px-3 py-2">
                  <dt className="text-amber-900/70">Network (ASN)</dt>
                  <dd className="font-mono text-amber-950">
                    AS{detail.asn}
                    {detail.org ? ` · ${detail.org}` : ""}
                  </dd>
                </div>
              ) : null}
            </dl>
            <div className="mt-5 flex flex-wrap gap-2">
              <Button
                onClick={onRetry}
                data-testid="auth-blocked-retry"
                className="bg-amber-800 hover:bg-amber-900 text-white rounded-full gap-2"
              >
                <RefreshCw size={14} /> Disable VPN &amp; try again
              </Button>
              <a
                href="mailto:support@hustleplaza.com?subject=VPN%20block%20on%20login"
                data-testid="auth-blocked-vpn-contact"
                className="inline-flex items-center px-4 h-10 rounded-full border border-amber-800/40 text-amber-900 text-sm hover:bg-amber-100"
              >
                Contact support
              </a>
            </div>
            <p className="mt-4 text-[11px] text-amber-900/60">
              If you don&apos;t use a VPN, share the IP and network above
              with support and we&apos;ll whitelist your connection.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ip_blocked
  const permanent = !!detail.permanent;
  const expiresIn = formatExpires(detail.expires_at);
  return (
    <div
      data-testid="auth-blocked-ip"
      className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-left"
    >
      <div className="flex items-start gap-3">
        <ShieldAlert className="w-6 h-6 text-rose-700 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h2 className="font-serif-display text-xl text-rose-900">
            {permanent
              ? "This IP has been permanently blocked"
              : "Too many failed sign-in attempts"}
          </h2>
          <p className="mt-2 text-sm text-rose-900/90 leading-relaxed">
            {permanent ? (
              <>
                We&apos;ve detected repeated credential-stuffing attempts
                from your network and locked it out. If this is a mistake,
                contact support and we&apos;ll investigate.
              </>
            ) : (
              <>
                Your network is temporarily blocked after repeated failed
                logins for accounts that don&apos;t exist. You&apos;ll be
                able to try again
                {expiresIn ? <strong> in ~{expiresIn}</strong> : " soon"}.
              </>
            )}
          </p>
          <dl className="mt-4 text-xs bg-white/60 rounded-lg border border-rose-200 divide-y divide-rose-100">
            <div className="flex justify-between px-3 py-2">
              <dt className="text-rose-900/70">Blocked IP</dt>
              <dd
                data-testid="auth-blocked-ip-ip"
                className="font-mono text-rose-950"
              >
                {detail.ip || "—"}
              </dd>
            </div>
            {detail.reason ? (
              <div className="flex justify-between px-3 py-2 gap-4">
                <dt className="text-rose-900/70">Reason</dt>
                <dd className="text-rose-950 text-right">{detail.reason}</dd>
              </div>
            ) : null}
          </dl>
          <div className="mt-5 flex flex-wrap gap-2">
            <a
              href="mailto:support@hustleplaza.com?subject=IP%20block%20on%20login"
              data-testid="auth-blocked-ip-contact"
              className="inline-flex items-center px-4 h-10 rounded-full bg-rose-800 hover:bg-rose-900 text-white text-sm"
            >
              Contact support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
